"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { DocumentUpload } from "@/components/documents/document-upload";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useJourneyData } from "@/hooks/use-journey-data";
import { QuoteForm } from "./quote-form";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
};

interface MortgageQuoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteId?: string;
  onDocumentUploaded: (docId: string | null) => Promise<void>;
}

// Add upload schema
const uploadSchema = z.object({
  file: z
    .instanceof(File)
    .refine(
      (file) => file.size <= MAX_FILE_SIZE,
      "File size must be less than 20MB",
    )
    .refine(
      (file) => Object.keys(ACCEPTED_FILE_TYPES).includes(file.type),
      "File type must be PDF, DOC, or DOCX",
    ),
  category: z.literal("mortgage"),
  type: z.literal("loan_estimate"),
});

export function MortgageQuoteDialog({
  open,
  onOpenChange,
  quoteId,
  onDocumentUploaded,
}: MortgageQuoteDialogProps) {
  const { mortgageQuotes = [] } = useJourneyData();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof uploadSchema>>({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      category: "mortgage",
      type: "loan_estimate",
    },
  });

  // Find existing quote data if editing
  const existingQuote = useMemo(
    () => (quoteId ? mortgageQuotes.find((q) => q.id === quoteId) : undefined),
    [quoteId, mortgageQuotes],
  );

  useEffect(() => {
    if (!open) {
      setIsProcessing(false);
      setError(null);
      setDocumentId(null);
    }
  }, [open]);

  // Handle document upload with proper validation
  const handleDocumentUploaded = async (docId: string | null) => {
    if (!docId) return;
    setIsProcessing(true);
    setError(null);
    setDocumentId(docId);

    try {
      await onDocumentUploaded(docId);
    } catch (error) {
      console.error("Error processing document:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to process document. Please try again or enter details manually.",
      );
      setDocumentId(null);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {quoteId ? "Edit Mortgage Quote" : "Add New Mortgage Quote"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          <AnimatePresence mode="wait">
            {!error && !isProcessing ? (
              quoteId ? (
                <QuoteForm
                  initialData={existingQuote}
                  onComplete={() => {
                    queryClient.invalidateQueries({ queryKey: ["buyer"] });
                    onOpenChange(false);
                  }}
                />
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-6"
                >
                  <div className="text-center p-6 space-y-4">
                    <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        Upload Loan Estimate
                      </h3>
                      <p className="text-muted-foreground text-sm mt-1">
                        We'll automatically extract the key details from your
                        Loan Estimate document
                      </p>
                    </div>
                  </div>

                  <DocumentUpload
                    value={documentId}
                    onChange={handleDocumentUploaded}
                    label="Loan Estimate Document"
                    category="mortgage"
                    type="loan_estimate"
                    strictType={true}
                    description="Upload the official Loan Estimate from your lender (PDF format)"
                    accept=".pdf"
                  />

                  <div className="text-center">
                    <Button variant="link" onClick={() => setError("manual")}>
                      Or enter details manually
                    </Button>
                  </div>
                </motion.div>
              )
            ) : isProcessing ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 text-center space-y-4"
              >
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-muted-foreground">
                  Processing your document...
                </p>
              </motion.div>
            ) : error === "manual" ? (
              <QuoteForm
                documentId={documentId}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["buyer"] });
                  onOpenChange(false);
                }}
              />
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                <Alert variant="destructive">
                  <AlertDescription className="flex flex-col gap-2">
                    <p>{error}</p>
                    <p className="text-sm">
                      This could happen if:
                      <ul className="list-disc pl-6 mt-1">
                        <li>The document is not a standard Loan Estimate</li>
                        <li>The document quality is too low</li>
                        <li>Required information is missing or unclear</li>
                      </ul>
                    </p>
                  </AlertDescription>
                </Alert>
                <div className="text-center">
                  <Button variant="link" onClick={() => setError("manual")}>
                    Enter details manually instead
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
